import {
  telemetrySchemaVersion,
  type TelemetryEnvelope,
  type TelemetryEvent,
  type TelemetryPort,
} from './telemetry-contract'

export const TELEMETRY_BROWSER_EVENT = 'cardreplay:telemetry'
const BATCH_SIZE = 10
const FLUSH_DELAY_MS = 5000
const MAX_BUFFER_SIZE = 100

type BrowserTelemetryOptions = Readonly<{
  endpoint: string | null
  sessionId?: string
}>

export class BrowserTelemetry implements TelemetryPort {
  private readonly endpoint: string | null
  private readonly sessionId: string
  private buffer: TelemetryEnvelope[] = []
  private flushTimer: number | null = null
  private sequence = 0
  private sending = false

  constructor({ endpoint, sessionId }: BrowserTelemetryOptions) {
    this.endpoint = endpoint
    this.sessionId = sessionId ?? createAnonymousSessionId()
    if (endpoint) {
      window.addEventListener('pagehide', this.flushUrgently)
      document.addEventListener('visibilitychange', this.flushWhenHidden)
    }
  }

  track(event: TelemetryEvent) {
    const safeEvent = sanitizeEvent(event)
    if (!safeEvent) return
    const envelope = Object.freeze({
      anonymousSessionId: this.sessionId,
      event: safeEvent,
      occurredAt: new Date().toISOString(),
      schemaVersion: telemetrySchemaVersion,
      sequence: ++this.sequence,
    })

    try {
      window.dispatchEvent(
        new CustomEvent(TELEMETRY_BROWSER_EVENT, { detail: envelope }),
      )
    } catch {
      // Diagnostics must never interrupt a user flow.
    }

    if (!this.endpoint) return
    this.buffer.push(envelope)
    if (this.buffer.length > MAX_BUFFER_SIZE) {
      this.buffer.splice(0, this.buffer.length - MAX_BUFFER_SIZE)
    }
    if (this.buffer.length >= BATCH_SIZE) void this.flush()
    else this.scheduleFlush()
  }

  async flush(urgent = false): Promise<void> {
    if (!this.endpoint || this.sending || this.buffer.length === 0) return
    this.cancelScheduledFlush()
    const batch = this.buffer.splice(
      0,
      urgent ? this.buffer.length : BATCH_SIZE,
    )
    const body = JSON.stringify({ events: batch })

    if (urgent && sendBeacon(this.endpoint, body)) return

    this.sending = true
    try {
      const response = await fetch(this.endpoint, {
        body,
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        keepalive: urgent,
        method: 'POST',
      })
      if (!response.ok)
        throw new Error('Telemetry transport rejected the batch')
    } catch {
      this.buffer = [...batch, ...this.buffer].slice(-MAX_BUFFER_SIZE)
    } finally {
      this.sending = false
      if (this.buffer.length > 0) this.scheduleFlush()
    }
  }

  dispose() {
    this.cancelScheduledFlush()
    if (this.endpoint) {
      window.removeEventListener('pagehide', this.flushUrgently)
      document.removeEventListener('visibilitychange', this.flushWhenHidden)
    }
  }

  private readonly flushUrgently = () => {
    void this.flush(true)
  }

  private readonly flushWhenHidden = () => {
    if (document.visibilityState === 'hidden') void this.flush(true)
  }

  private scheduleFlush() {
    if (this.flushTimer !== null) return
    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = null
      void this.flush()
    }, FLUSH_DELAY_MS)
  }

  private cancelScheduledFlush() {
    if (this.flushTimer === null) return
    window.clearTimeout(this.flushTimer)
    this.flushTimer = null
  }
}

function sanitizeEvent(event: TelemetryEvent): TelemetryEvent | null {
  const context = sanitizeContext(event.data)
  if (!context) return null

  if (event.name === 'learning.session_viewed') {
    const cardCount = safeCount(event.data.cardCount)
    return cardCount === null
      ? null
      : freeze({ name: event.name, data: { ...context, cardCount } })
  }
  if (event.name === 'learning.playback_changed') {
    if (
      !includes(['completed', 'idle', 'paused', 'playing'], event.data.playback)
    ) {
      return null
    }
    return freeze({
      name: event.name,
      data: { ...context, playback: event.data.playback },
    })
  }
  if (event.name === 'learning.grade_finished') {
    if (
      !includes(['harder', 'master'], event.data.intent) ||
      !includes(['failed', 'succeeded'], event.data.outcome)
    ) {
      return null
    }
    return freeze({
      name: event.name,
      data: {
        ...context,
        intent: event.data.intent,
        outcome: event.data.outcome,
      },
    })
  }
  if (
    (event.name === 'learning.undo_finished' ||
      event.name === 'learning.reset_finished') &&
    includes(['failed', 'succeeded'], event.data.outcome)
  ) {
    return freeze({
      name: event.name,
      data: { ...context, outcome: event.data.outcome },
    })
  }
  return null
}

function sanitizeContext(data: TelemetryEvent['data']) {
  if (
    !includes(['difficult', 'mastered', 'studying'], data.bucket) ||
    !includes(['auto-play', 'flashcards'], data.modeId) ||
    !Number.isSafeInteger(data.modeVersion) ||
    data.modeVersion < 1 ||
    !includes(['group', 'personal', 'product'], data.sourceKind)
  ) {
    return null
  }
  return {
    bucket: data.bucket,
    modeId: data.modeId,
    modeVersion: data.modeVersion,
    sourceKind: data.sourceKind,
  } as const
}

function safeCount(value: number): number | null {
  return Number.isSafeInteger(value) && value >= 0 && value <= 1_000_000
    ? value
    : null
}

function includes<const TValue extends string>(
  values: readonly TValue[],
  value: unknown,
): value is TValue {
  return typeof value === 'string' && values.includes(value as TValue)
}

function freeze<TEvent extends TelemetryEvent>(event: TEvent): TEvent {
  Object.freeze(event.data)
  return Object.freeze(event)
}

function createAnonymousSessionId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `session-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  }
}

function sendBeacon(endpoint: string, body: string): boolean {
  try {
    return navigator.sendBeacon(
      endpoint,
      new Blob([body], { type: 'application/json' }),
    )
  } catch {
    return false
  }
}
